import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "/logo.png";

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-white hover:bg-white/20">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <img src={LOGO_URL} alt="My Wishlists" className="h-12" />
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl font-bold text-gray-800">Privacy Policy</h1>
          </div>
          
          <p className="text-sm text-gray-500 mb-8">Last Updated: December 3, 2024</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">1. Introduction</h2>
              <p>
                My Wishlists ("we", "our", or "us") operates www.mywishlists.in. This Privacy Policy explains how we collect, use, disclose, and protect your personal information in accordance with the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Personal Information</h3>
              <p className="mb-3">When you use our service, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and email address (via Google Authentication)</li>
                <li>Profile information from your Google account</li>
                <li>Wishlist and event information you create</li>
                <li>Product links and images you add to wishlists</li>
                <li>Information about gifts selected by you or others</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Usage data and analytics</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide and maintain our service</li>
                <li>Authenticate your identity</li>
                <li>Enable wishlist creation and sharing</li>
                <li>Facilitate gift selection and surprise management</li>
                <li>Send service-related notifications</li>
                <li>Improve and optimize our service</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">4. Data Sharing and Disclosure</h2>
              <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Event Collaborators:</strong> When you add collaborators to your events or wishlists</li>
                <li><strong>Service Providers:</strong> Third-party services that help us operate (e.g., hosting, analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Google OAuth:</strong> For authentication purposes only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">5. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Encrypted data transmission (HTTPS/SSL)</li>
                <li>Secure authentication via Google OAuth</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
                <li>Secure data storage practices</li>
              </ul>
              <p className="mt-3">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">6. Your Rights</h2>
              <p className="mb-3">Under Indian law, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for data processing</li>
                <li>Export your data</li>
                <li>Lodge a complaint with authorities</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at support@mywishlists.in
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">7. Data Retention</h2>
              <p>
                We retain your personal information only as long as necessary to provide our services and comply with legal obligations. You may delete your account at any time, which will remove your personal data from our active systems.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">8. Cookies and Tracking</h2>
              <p className="mb-3">
                We use cookies and similar technologies to enhance your experience. You can control cookie preferences through your browser settings.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">9. Third-Party Links</h2>
              <p>
                Our service may contain links to third-party websites (e.g., Amazon, Myntra). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">10. Children's Privacy</h2>
              <p>
                Our service is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected data from a minor, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of the service constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">12. Governing Law</h2>
              <p>
                This Privacy Policy is governed by the laws of India. Any disputes arising from this policy shall be subject to the exclusive jurisdiction of courts in Bangalore, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">13. Contact Us</h2>
              <p className="mb-3">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold">My Wishlists</p>
                <p>Email: support@mywishlists.in</p>
                <p>Address: 201-A, NKEA, Sarjapur Road, Bangalore, India</p>
              </div>
            </section>

            <section className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <p className="font-semibold text-gray-800 mb-2">Note:</p>
              <p className="text-sm">
                This Privacy Policy complies with the Information Technology Act, 2000 and associated rules. By using My Wishlists, you consent to the collection and use of information as described in this policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
